import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Form, Input, Select, message } from 'antd';
import FormLayoutGroup from 'components/ui/form-layout-group';
import 'styles/ant-design.css';
import { useTranslation } from 'react-i18next';
import { fields, getValidationSchema, initialValues } from './form';
import { handleError, validateForm } from 'utils/form';
import FormActionButtons from 'components/ui/form-action-buttons';
import { useMutation, useQueryClient } from 'react-query';
import { contractApi } from 'services/apis';
import { EQueryKeys } from 'constants/query-keys';
import { SortedSelect } from 'components/ui/sorted-select';

const { TextArea } = Input;

interface NoticeAndCommentFormProps {
  contractId: string;
  noticePeriod?: number;
  financeComments?: string;
}

const NoticeAndCommentForm: React.FC<NoticeAndCommentFormProps> = ({
  contractId,
  noticePeriod,
  financeComments,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const hasFormBeenUpdated = useRef(false);
  const queryClient = useQueryClient();

  const { mutate: updateContractPremiumProcessingMutation, isLoading } =
    useMutation({
      mutationFn: (payload: {
        noticeOfCancellationPeriod?: number;
        financeComment?: string;
      }) =>
        contractApi.updateContractPremiumProcessingData({
          contractId,
          updateContractPremiumProcessingDataPayloadDto: payload,
        }),
      onSuccess: () => {
        message.success(t('premiumProcessing.recordUpdatedSuccessfully'));
        queryClient.invalidateQueries([
          EQueryKeys.GET_ORIGINAL_PREMIUM_SCHEDULE,
          contractId,
        ]);
      },
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('premiumProcessing.recordUpdatedFailed'),
        });
      },
    });

  const handleOnFinish = useCallback(
    async (values: { noticePeriod: number; financeComments: string }) => {
      const payload = {
        noticeOfCancellationPeriod: values.noticePeriod,
        financeComment: values.financeComments,
      };

      updateContractPremiumProcessingMutation(payload);
    },
    [updateContractPremiumProcessingMutation]
  );

  const initialData = useMemo(
    () => ({
      noticePeriod: noticePeriod ? noticePeriod : initialValues.noticePeriod,
      financeComments,
    }),
    [noticePeriod, financeComments]
  );

  useEffect(() => {
    if (hasFormBeenUpdated.current) return;

    if (initialData) {
      form.setFieldsValue(initialData);
      validateForm(initialData, form, fields, getValidationSchema()).then(
        setIsFormValid
      );
    }
  }, [form, initialData, t]);

  return (
    <Form
      layout="vertical"
      className="mt-8"
      form={form}
      initialValues={initialValues}
      onValuesChange={async () => {
        const values = form.getFieldsValue();
        const isValid = await validateForm(
          values,
          form,
          fields,
          getValidationSchema()
        );
        setIsFormValid(isValid);
      }}
      onFinish={handleOnFinish}
    >
      <FormLayoutGroup mdColumns={1}>
        <h2 className="text-[#010D1D] text-md flex gap-2 items-center font-medium mb-2">
          {t('premiumProcessing.noticePeriod')}
        </h2>
        <Form.Item name="noticePeriod" className="w-[260px]">
          <SortedSelect
            size="large"
            options={[
              { value: 15, label: t('premiumProcessing.days', { days: 15 }) },
              { value: 30, label: t('premiumProcessing.days', { days: 30 }) },
              { value: 45, label: t('premiumProcessing.days', { days: 45 }) },
              { value: 60, label: t('premiumProcessing.days', { days: 60 }) },
            ]}
            allowClear
          />
        </Form.Item>
      </FormLayoutGroup>
      <FormLayoutGroup mdColumns={1}>
        <h2 className="text-[#010D1D] text-md flex gap-2 items-center font-medium mb-2">
          {t('premiumProcessing.financeComments')}
        </h2>
        <Form.Item name="financeComments">
          <TextArea
            rows={4}
            placeholder={t('premiumProcessing.addYourCommentsHere')}
            size="large"
          />
        </Form.Item>
      </FormLayoutGroup>

      <div className="mt-6">
        <FormActionButtons
          confirmText={t('general.save')}
          isConfirmDisabled={!isFormValid || isLoading}
          includeCancel={false}
        />
      </div>
    </Form>
  );
};

export default NoticeAndCommentForm;
